"use client";

import React from "react";
import { Container, AppButton } from "@/components";
import { motion } from "framer-motion";
import { Home, RefreshCcw } from "lucide-react";

// --- Smoky Letter Component ---
const SmokyLetter = ({
  char,
  index,
  baseDelay,
  colorClass = "text-black",
  fontClass = "font-black",
}: {
  char: string;
  index: number;
  baseDelay: number;
  colorClass?: string;
  fontClass?: string;
}) => {
  const smokeVariants = {
    initial: {
      rotate: 45,
      y: -150,
      opacity: 0,
      filter: "blur(20px)",
    },
    animate: {
      rotate: 0,
      y: 0,
      opacity: 1,
      filter: "blur(0px)",
    },
  };

  return (
    <motion.span
      variants={smokeVariants}
      initial="initial"
      animate="animate"
      transition={{
        duration: 1.5,
        ease: "easeOut",
        delay: baseDelay + index * 0.03,
      }}
      className={`inline-block whitespace-pre ${fontClass} ${colorClass}`}
    >
      {char}
    </motion.span>
  );
};

// --- SmokySentence ---
const SmokySentence = ({
  text,
  baseDelay = 0,
  colorClass,
  fontClass,
  className = "",
}: {
  text: string;
  baseDelay?: number;
  colorClass?: string;
  fontClass?: string;
  className?: string;
}) => {
  const words = text.split(" ");

  return (
    <div className={`flex flex-wrap justify-center items-center ${className}`}>
      {words.map((word, wordIndex) => {
        const letters = word.split("");
        const previousCharsLength = words.slice(0, wordIndex).join(" ").length;
        const currentWordStart = wordIndex === 0 ? 0 : previousCharsLength + 1;

        return (
          <span
            key={wordIndex}
            className="inline-block whitespace-nowrap mr-[0.3em]"
          >
            {letters.map((char, letterIndex) => (
              <SmokyLetter
                key={letterIndex}
                char={char}
                index={currentWordStart + letterIndex}
                baseDelay={baseDelay}
                colorClass={colorClass}
                fontClass={fontClass}
              />
            ))}
          </span>
        );
      })}
    </div>
  );
};

const NotFound = () => {
  return (
    <>
      {/* Hiding closing block */}
      <style
        dangerouslySetInnerHTML={{
          __html: "#closing-block { display: none !important; }",
        }}
      />
      <section className="min-h-[70vh] flex items-center justify-center bg-white py-20 overflow-hidden text-center">
        <Container className="px-4 lg:px-10 flex flex-col items-center">
          {/* 404 Background - with Red Smoke */}
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 0.15, scale: 1 }}
            className="flex items-center justify-center pointer-events-none mb-4 select-none"
          >
            <SmokySentence
              text="404"
              baseDelay={0}
              colorClass="text-red-800"
              fontClass="font-black text-5xl lg:text-[12vw] leading-none"
            />
          </motion.div>

          <div className="relative z-10 space-y-8 flex flex-col items-center">
            {/* Main Title - Single Sentence Focus */}
            <div className="text-3xl lg:text-6xl font-black uppercase tracking-tighter leading-tight flex flex-wrap justify-center">
              <SmokySentence text="Lost in the " baseDelay={0.5} />
              <SmokySentence
                text="Pixels."
                baseDelay={0.8}
                colorClass="text-primary"
              />
            </div>

            {/* Subtext - Smoky Reveal */}
            <div className="max-w-md mx-auto">
              <SmokySentence
                text="The data you are looking for has been moved or doesn't exist in our current digital registry."
                colorClass="text-muted-foreground"
                fontClass="text-lg font-medium"
                baseDelay={0.5}
              />
            </div>

            {/* Buttons  */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 2.5, duration: 1 }}
              className="flex flex-col sm:flex-row items-center justify-center gap-6 pt-4"
            >
              <AppButton
                onClick={() => window.location.reload()}
                buttonClass="h-10 px-10 bg-white text-black"
                icon={<RefreshCcw size={18} />}
              >
                Try Again
              </AppButton>

              <AppButton
                href="/"
                buttonClass="h-10 px-10"
                shadowClass="bg-black"
              >
                <Home className="w-4 h-4" />
                <span className=""> Return Home</span>
              </AppButton>
            </motion.div>
          </div>
        </Container>
      </section>
    </>
  );
};

export default NotFound;
